// API 攔截器：所有後端請求的唯一出入口。取代舊版各處手寫的 fetch + res2json + throw new Error(r.data?.error)。
//
// 統一處理：
//   - 401 → 登出並立即重載（同舊版 res2json）；下載等不想被打斷的請求傳 skipAuthHandling: true
//   - 非 2xx → 拋 ApiError，message 取後端 data.error（= form ns 的 i18n key，見 ./apiError.js）；
//     後端沒給頂層 error 時 message 為空字串，顯示時會退回「Failed」（同舊版 throw new Error(r.data?.error)）。
//     舊版少數地方會往下找巢狀欄位錯誤，那些呼叫端自己用 firstErrorMessage(err.data)
//   - 204 → 回 null；成功時回解析後的 JSON 本體（不再包 { status, data }）；
//     要分辨 200／202 的呼叫端（例：匯出判斷 202）傳 withStatus: true，改回 { status, data }
//   - params 物件轉 query string；body 是一般物件就自動 JSON.stringify，FormData 原樣送
//   - signal 往下傳，讓 TanStack Query 能取消過期請求
// 刻意不 import i18n：登入頁也會用到 request，引用主語言包會把整包帶進登入頁。錯誤文案在 ./apiError.js
import { account, logout } from './auth'

export class ApiError extends Error {
  constructor(status, message, data) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

function buildUrl(url, params) {
  if (!params) return url
  const qs = params instanceof URLSearchParams ? params : new URLSearchParams()
  if (!(params instanceof URLSearchParams)) {
    for (const [key, value] of Object.entries(params)) {
      if (value == null || value === '') continue
      // 陣列送成重複 key（?a=1&a=2）；個別端點若要逗號串接等格式，呼叫端自己組好字串再傳
      if (Array.isArray(value)) value.forEach((v) => qs.append(key, String(v)))
      else qs.append(key, String(value))
    }
  }
  const s = qs.toString()
  return s ? `${url}${url.includes('?') ? '&' : '?'}${s}` : url
}

async function parseBody(res) {
  if (res.status === 204) return null
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

// 後端欄位級驗證錯誤沒有頂層 error（如 { pricing: [null, null, { gte: "..." }] }），往下找第一個字串。
// 全站唯一一份：舊版各頁各寫一份，React 版統一從這裡 import
export function firstErrorMessage(obj) {
  if (obj == null) return null
  if (typeof obj === 'string') return obj
  if (typeof obj === 'object') {
    for (const v of Object.values(obj)) {
      const m = firstErrorMessage(v)
      if (m) return m
    }
  }
  return null
}

// responseType: 'json'（預設）| 'blob'（下載檔案）| 'text'
export async function request(url, { method = 'GET', params, body, signal, headers, responseType = 'json', skipAuthHandling = false, withStatus = false } = {}) {
  const init = { method, signal, headers: { ...headers } }
  if (body instanceof FormData) {
    init.body = body
  } else if (body !== undefined) {
    init.body = JSON.stringify(body)
    init.headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(buildUrl(url, params), init)

  if (res.status === 401 && !skipAuthHandling) {
    if (account.username) {
      logout()
      document.location.reload()
    }
    throw new ApiError(401, '', null)
  }

  if (!res.ok) {
    const data = await parseBody(res)
    // 非 JSON（如 HTML 錯誤頁）不拿來當訊息
    const message = data && typeof data === 'object' && typeof data.error === 'string' ? data.error : ''
    throw new ApiError(res.status, message, data)
  }
  if (responseType === 'blob') return res.blob()
  if (responseType === 'text') return res.text()
  if (withStatus) return { status: res.status, data: await parseBody(res) }
  return parseBody(res)
}

request.get = (url, opts) => request(url, { ...opts, method: 'GET' })
request.post = (url, body, opts) => request(url, { ...opts, method: 'POST', body })
request.put = (url, body, opts) => request(url, { ...opts, method: 'PUT', body })
request.patch = (url, body, opts) => request(url, { ...opts, method: 'PATCH', body })
request.delete = (url, opts) => request(url, { ...opts, method: 'DELETE' })

// 查詢遇 HTTP 錯誤時改回預設值：用在舊版「讀取失敗就靜默、畫面落到空值」的 queryFn。
// statuses：只有這些狀態碼才回預設值（例：[403] 無權限當空清單，其他錯誤照拋讓畫面出聲）；省略＝所有 HTTP 錯誤。
// 401（已登出重載）與網路層錯誤一律照常拋出；寫入一律用 useMutation 的 onError，不用這個
export function withHttpFallback(promise, fallback, statuses) {
  return promise.catch((err) => {
    if (err instanceof ApiError && err.status !== 401 && (!statuses || statuses.includes(err.status))) return fallback
    throw err
  })
}
